/**
 * The stories behind the Week 4 map toys, for logistic regression: the
 * sigmoid slide's shape, the sorting line's items and their chances of
 * being a sneaker, and the doodle machine's weights. Every probability here
 * is σ of a weighted sum, as in Lesson 8.
 */

/**
 * The sigmoid, σ(z) = 1 / (1 + e^(−z)).
 *
 * @param z - The weighted sum.
 * @return The probability, in the open interval (0, 1).
 * @throws Never.
 */
export function sigmoid(z: number): number {
  return 1 / (1 + Math.exp(-z));
}

/**
 * Writes a probability with two decimals, never as 0.00 or 1.00, since the
 * sigmoid never reaches either.
 *
 * @param probability - A probability in (0, 1).
 * @return The probability as text, between "0.01" and "0.99".
 * @throws Never.
 */
export function formatProbability(probability: number): string {
  return Math.min(0.99, Math.max(0.01, probability)).toFixed(2);
}

/**
 * The sigmoid slide, in its toy's viewBox units: the bed's low end sits at
 * `lowX`, its high end at `highX`, and across that span z runs over the
 * closed interval [−`zReach`, `zReach`].
 */
export const SIGMOID_SLIDE = {
  groundY: 86,
  lowX: 14,
  highX: 100,
  zReach: 6,
  /** Height of the bed above the ground where σ is 0. */
  baseHeight: 8,
  /** How much higher the bed climbs where σ is 1. */
  rise: 62,
  /** How far above the bed a rider's centre sits. */
  seat: 6,
} as const;

/**
 * The y of the slide's bed at x, so the bed's height above the ground is
 * `baseHeight + rise × σ(z)`.
 *
 * @param x - A point in the closed interval [`lowX`, `highX`]; points
 *     outside it are clamped to the nearer end.
 * @return The bed's y in viewBox units (smaller is higher).
 * @throws Never.
 */
export function slideBedY(x: number): number {
  const {groundY, lowX, highX, zReach, baseHeight, rise} = SIGMOID_SLIDE;
  const clamped = Math.min(highX, Math.max(lowX, x));
  const z = ((clamped - (lowX + highX) / 2) / (highX - lowX)) * 2 * zReach;
  return groundY - (baseHeight + rise * sigmoid(z));
}

/** Where a rider is at one moment of a ride down the slide. */
export interface SlideStop {
  /** How far through the ride, in percent from 0 to 100. */
  percent: number;
  x: number;
  y: number;
  /** The tilt that keeps the rider on the bed, in degrees. */
  angle: number;
}

/**
 * The stops of one ride, from the high end to the low end. The rider's x
 * eases in and out, so they push off slowly, are fastest through the steep
 * middle where σ climbs fastest, and coast to a stop on the flat.
 *
 * @param params.count - How many stops. Omitted means 25.
 * @return The stops, first at the top and last at the bottom.
 * @throws Never.
 */
export function slideStops({count = 25}: {count?: number} = {}): SlideStop[] {
  const {lowX, highX, seat} = SIGMOID_SLIDE;
  const start = highX + 2;
  const end = lowX + 4;
  const stops: SlideStop[] = [];
  for (let index = 0; index < count; index += 1) {
    const fraction = index / (count - 1);
    const eased = fraction * fraction * (3 - 2 * fraction);
    const x = start + (end - start) * eased;
    const ahead = slideBedY(x - 0.5);
    const behind = slideBedY(x + 0.5);
    stops.push({
      percent: fraction * 100,
      x,
      y: slideBedY(x) - seat,
      angle: (Math.atan2(behind - ahead, 1) * 180) / Math.PI,
    });
  }
  return stops;
}

/** What can ride the sorting line. */
export type SortingKind =
  | 'sneaker'
  | 'sandal'
  | 'boot'
  | 'tee'
  | 'bag'
  | 'ollie';

/** One kind of item on the sorting line. */
interface SortingItemKind {
  kind: SortingKind;
  /** The item's name in an announcement. */
  name: string;
  /** How often it comes down the line, relative to the others. */
  weight: number;
  /** The mean of its weighted sum z; each item draws its own around it. */
  meanScore: number;
  /** How far one item's z strays from the mean (a standard deviation). */
  spread: number;
}

const SNEAKER: SortingItemKind = {
  kind: 'sneaker',
  name: 'a sneaker',
  weight: 8,
  meanScore: 2.4,
  spread: 0.9,
};

/**
 * The items that come down the sorting line. Sandals share a sneaker's
 * outline, so the machine calls about half of them sneakers.
 */
export const SORTING_ITEMS: readonly SortingItemKind[] = [
  SNEAKER,
  {kind: 'sandal', name: 'a sandal', weight: 3, meanScore: 0.2, spread: 1},
  {kind: 'boot', name: 'an ankle boot', weight: 3, meanScore: -1, spread: 0.9},
  {kind: 'tee', name: 'a T-shirt', weight: 3, meanScore: -3.6, spread: 0.5},
  {kind: 'bag', name: 'a bag', weight: 3, meanScore: -2.8, spread: 0.6},
  {kind: 'ollie', name: 'Ollie', weight: 1, meanScore: -3.9, spread: 0.2},
];

/** One item the machine has sorted. */
export interface SortedItem {
  kind: SortingKind;
  /** The item's name in an announcement. */
  name: string;
  /** What the machine says: P(sneaker | the item). */
  probability: number;
  /** Whether the gate dropped it in the sneaker bin (probability ≥ 0.5). */
  isCalledSneaker: boolean;
  /** Whether the call was right. */
  isRight: boolean;
}

/**
 * Draws a standard normal value (Box–Muller).
 *
 * @param random - Returns a uniform value in [0, 1).
 * @return The value.
 */
function standardNormal(random: () => number): number {
  const u = 1 - random();
  const v = random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/**
 * Picks a kind of item, each as often as its weight says.
 *
 * @param random - Returns a uniform value in [0, 1).
 * @return The kind.
 */
function pickSortingKind(random: () => number): SortingItemKind {
  const total = SORTING_ITEMS.reduce((sum, item) => sum + item.weight, 0);
  let pick = random() * total;
  for (const item of SORTING_ITEMS) {
    pick -= item.weight;
    if (pick < 0) {
      return item;
    }
  }
  return SNEAKER;
}

/**
 * Sends the next item down the line: picks a kind by weight, draws its z,
 * and lets the gate send it to the sneaker bin when σ(z) ≥ 0.5.
 *
 * @param params.random - Returns a uniform value in [0, 1).
 * @return The sorted item.
 * @throws Never.
 */
export function sortNextItem({random}: {random: () => number}): SortedItem {
  const {kind, name, meanScore, spread} = pickSortingKind(random);
  const probability = sigmoid(meanScore + spread * standardNormal(random));
  const isCalledSneaker = probability >= 0.5;
  return {
    kind,
    name,
    probability,
    isCalledSneaker,
    isRight: isCalledSneaker === (kind === 'sneaker'),
  };
}

/** A part of a doodle the machine reads, with its weight. */
export type DoodleFeature =
  | 'pointyEars'
  | 'roundEars'
  | 'eyes'
  | 'whiskers'
  | 'mane'
  | 'snout';

/**
 * The doodle machine's bias θ₀: what it says about a bare head, before any
 * feature is drawn.
 */
export const DOODLE_BIAS = -1.5;

/**
 * The doodle machine's weights. Every doodle has eyes, so eyes tell a cat
 * from a bear no better than nothing, and their weight is 0.
 */
export const DOODLE_WEIGHTS: Readonly<Record<DoodleFeature, number>> = {
  pointyEars: 2,
  roundEars: -0.5,
  eyes: 0,
  whiskers: 2.4,
  mane: -2.4,
  snout: -1.6,
};

/** How a feature is named in an announcement. */
export const DOODLE_FEATURE_NAMES: Readonly<Record<DoodleFeature, string>> = {
  pointyEars: 'pointy ears',
  roundEars: 'round ears',
  eyes: 'eyes',
  whiskers: 'whiskers',
  mane: 'a mane',
  snout: 'a snout',
};

/** Who a doodle turns out to be. */
export type DoodleSubject = 'cat' | 'bear' | 'lion' | 'ollie';

/** One doodle: who it is and the features drawn, in order. */
export interface Doodle {
  subject: DoodleSubject;
  /** The subject's name in an announcement. */
  name: string;
  features: readonly DoodleFeature[];
}

/** The doodles, each drawn a feature per press after the head. */
export const DOODLES: Readonly<Record<DoodleSubject, Doodle>> = {
  cat: {
    subject: 'cat',
    name: 'a cat',
    features: ['pointyEars', 'eyes', 'whiskers'],
  },
  bear: {
    subject: 'bear',
    name: 'a bear',
    features: ['roundEars', 'eyes', 'snout'],
  },
  lion: {
    subject: 'lion',
    name: 'a lion',
    features: ['eyes', 'whiskers', 'mane'],
  },
  ollie: {
    subject: 'ollie',
    name: 'Ollie',
    features: ['roundEars', 'eyes', 'whiskers'],
  },
};

/**
 * What the doodle machine says once the first `drawn` features are on the
 * page: σ(θ₀ + the sum of their weights).
 *
 * @param params.doodle - The doodle.
 * @param params.drawn - How many of its features are drawn, from 0 (the
 *     bare head) to all of them; larger counts read every feature.
 * @return P(cat | the features drawn).
 * @throws Never.
 */
export function doodleProbability({
  doodle,
  drawn,
}: {
  doodle: Doodle;
  drawn: number;
}): number {
  const score = doodle.features
    .slice(0, Math.max(0, drawn))
    .reduce((sum, feature) => sum + DOODLE_WEIGHTS[feature], DOODLE_BIAS);
  return sigmoid(score);
}

/**
 * Picks the next doodle: a cat half the time, else a bear or a lion, and
 * Ollie now and then; never the same non-cat twice in a row.
 *
 * @param params.random - Returns a uniform value in [0, 1).
 * @param params.previous - The doodle just finished. Omitted means none.
 * @return The next doodle's subject.
 * @throws Never.
 */
export function nextDoodle({
  random,
  previous,
}: {
  random: () => number;
  previous?: DoodleSubject;
}): DoodleSubject {
  const roll = random();
  const subject: DoodleSubject =
    roll < 0.5 ? 'cat' : roll < 0.7 ? 'bear' : roll < 0.88 ? 'lion' : 'ollie';
  if (subject !== 'cat' && subject === previous) {
    return 'cat';
  }
  return subject;
}
